import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import PageContainer from '../../components/PageContainer';
import { themeCssVars } from '../../theme/cssVars';
import './PrintFileReview.css';
import {
  getPrintFileBatches,
  IPrintFileBatchesResponse,
  IPrintFileBatchSummary,
  PRINT_FILE_BATCH_STATUSES,
  PrintFileBatchStatus,
} from '../../api/printFileBatches';
import { batchDownloadActivity, downloadBatchAssets, packDownloadProgress } from '../../lib/printFileReview/assets';
import {
  autoPurgeOldBatches,
  createBatch,
  createNextBatch,
  loadOwnPack,
  loadPackForViewing,
  openBatch,
  pruneExpiredPacks,
  purgeBatchImages,
  redownloadPack,
  removeLocalPack,
  resumeDownloads,
  syncOwnPackFromServer,
  syncOwnPacksFromServer,
} from '../../lib/printFileReview/batches';
import { downloadBatchLogs } from '../../lib/printFileReview/logs';
import { kickSync, MAX_AUTO_ATTEMPTS, retryAllNow, setRetriesStopped } from '../../lib/printFileReview/sync';
import { ILocalBatch, ILocalFile } from '../../lib/printFileReview/types';
import { IStore } from '../../store/types';
import PackStatsPanel from './PackStatsPanel';
import ReviewModal from './ReviewModal';
import { usePrintReviewData } from './usePrintReviewData';

const SERVER_PAGE_SIZE = 20;
/** "All packs" refreshes itself this often while the page is open. */
const SERVER_REFRESH_MS = 60 * 1000;
/** nextAttemptAt of saves that wait for a manual retry. */
const PARKED = Number.MAX_SAFE_INTEGER;

const STATUS_STYLE: Record<PrintFileBatchStatus, { label: string; cls: string }> = {
  init: { label: 'Created', cls: 'bg-slate-600/60 text-gray-200' },
  in_progress: { label: 'In progress', cls: 'bg-blue-600/40 text-blue-100' },
  done: { label: 'Done', cls: 'bg-green-600/40 text-green-100' },
};

const fmtDate = (value: number | string | null | undefined) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const fmtBytes = (n: number) => {
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(1)} GB`;
  if (n >= 10 * 1024 ** 2) return `${Math.round(n / 1024 ** 2)} MB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${Math.round(n / 1024)} KB`;
};

const batchStats = (files: ILocalFile[]) => ({
  total: files.length,
  decided: files.filter((f) => f.decision).length,
  ok: files.filter((f) => f.decision === 'ok').length,
  design: files.filter((f) => f.decision === 'need_design').length,
  cs: files.filter((f) => f.decision === 'need_cs').length,
  edited: files.filter((f) => f.hasManualEdits).length,
  synced: files.filter((f) => f.decision && f.sync.state === 'synced').length,
  syncErrors: files.filter((f) => f.sync.state === 'error').length,
});

const ProgressBar: React.FC<{ done: number; total: number }> = ({ done, total }) => (
  <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
    <div
      className="h-full bg-gradient-to-r from-blue-500 to-violet-500 transition-all duration-300"
      style={{ width: `${total ? Math.round((done / total) * 100) : 0}%` }}
    />
  </div>
);

const StatusChip: React.FC<{ status: PrintFileBatchStatus }> = ({ status }) => {
  const s = STATUS_STYLE[status] ?? { label: status, cls: 'bg-slate-600/60 text-gray-200' };
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${s.cls}`}>{s.label}</span>;
};

const smallBtn = 'px-2.5 py-1 rounded-lg text-xs font-medium border-0 transition-colors disabled:opacity-40 disabled:cursor-not-allowed';

const PrintFileReviewPage = () => {
  const { loading, error, batches: allLocalBatches, filesByBatch, outbox, retriesStopped } = usePrintReviewData();
  const { role, name: authName } = useSelector<IStore, IStore['auth']['data']>((state) => state.auth.data);
  // Colors only: the page keeps its own markup and dark look; light mode is a CSS override keyed on data-theme.
  const theme = useTheme();
  const cssVars = useMemo(() => themeCssVars(theme.custom) as React.CSSProperties, [theme]);
  // Moderators/admins may look at any pack; designers only at their own.
  const canViewAny = role === 'admin' || role === 'moderator';
  const myName = (authName || '').trim().toLowerCase();
  // Packs loaded only for viewing are not this designer's work: keep them out of the local list.
  const batches = useMemo(() => allLocalBatches.filter((b) => !b.viewOnly), [allLocalBatches]);
  const [loadingPackId, setLoadingPackId] = useState<string | null>(null);
  const [localFilter, setLocalFilter] = useState<'all' | PrintFileBatchStatus | null>(null);
  const localCounts = useMemo(() => {
    const counts: Record<PrintFileBatchStatus, number> = { init: 0, in_progress: 0, done: 0 };
    batches.forEach((b) => { counts[b.serverStatus] = (counts[b.serverStatus] ?? 0) + 1; });
    return counts;
  }, [batches]);
  // Until the designer picks a filter: packs in progress, else packs not started yet, else all.
  const activeLocalFilter = localFilter ?? (localCounts.in_progress ? 'in_progress' : localCounts.init ? 'init' : 'all');
  const shownLocal = activeLocalFilter === 'all' ? batches : batches.filter((b) => b.serverStatus === activeLocalFilter);
  // Moderator statistics row opened under a pack in "All packs".
  const [statsOpenId, setStatsOpenId] = useState<string | null>(null);
  // "Remove" asks for a second click before it deletes a set from this browser.
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeBatchId = searchParams.get('set');
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState<{ tone: 'info' | 'error'; text: string } | null>(null);
  const [storage, setStorage] = useState<{ usage: number; quota: number } | null>(null);

  const [serverStatus, setServerStatus] = useState<'all' | PrintFileBatchStatus>('all');
  const [serverPage, setServerPage] = useState(1);
  const [server, setServer] = useState<{ loading: boolean; error: string | null; data: IPrintFileBatchesResponse | null }>({
    loading: true,
    error: null,
    data: null,
  });
  const [serverUpdatedAt, setServerUpdatedAt] = useState<number | null>(null);
  const serverUpdatedAtRef = useRef(0);

  // Background work survives navigation: resume it whenever the page is opened.
  useEffect(() => {
    kickSync();
    void resumeDownloads();
    void autoPurgeOldBatches();
    void syncOwnPacksFromServer();
  }, []);

  /** `silent` — background refresh: no loading state, and a failure keeps the last list. */
  const loadServer = useCallback(async (silent = false) => {
    if (!silent) setServer((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await getPrintFileBatches({
        page: serverPage,
        limit: SERVER_PAGE_SIZE,
        sortDirection: 'DESC',
        status: serverStatus === 'all' ? undefined : [serverStatus],
      });
      setServer({ loading: false, error: null, data });
      serverUpdatedAtRef.current = Date.now();
      setServerUpdatedAt(serverUpdatedAtRef.current);
    } catch (e: any) {
      const failed = { loading: false, error: e?.message || 'Failed to load sets', data: null };
      if (silent) setServer((s) => (s.data ? { ...s, loading: false } : failed));
      else setServer(failed);
    }
  }, [serverPage, serverStatus]);

  useEffect(() => {
    void loadServer();
  }, [loadServer]);

  // Keep "All packs" fresh while the page is open; paused in a background tab and
  // caught up as soon as the tab is visible again.
  useEffect(() => {
    const refresh = () => {
      if (!document.hidden) void loadServer(true);
    };
    const timer = window.setInterval(refresh, SERVER_REFRESH_MS);
    const onVisible = () => {
      if (!document.hidden && Date.now() - serverUpdatedAtRef.current >= SERVER_REFRESH_MS) void loadServer(true);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [loadServer]);

  // The server deletes packs that stay "init" for 30 minutes — drop them from this browser too.
  useEffect(() => {
    let alive = true;
    const prune = async (force = false) => {
      if (document.hidden && !force) return;
      try {
        const removed = await pruneExpiredPacks();
        if (alive && removed.length) {
          setMessage({
            tone: 'info',
            text: `Set${removed.length > 1 ? 's' : ''} #${removed.join(', #')} ${removed.length > 1 ? 'were' : 'was'} not started within 30 minutes — the server deleted ${removed.length > 1 ? 'them' : 'it'}, so ${removed.length > 1 ? 'they were' : 'it was'} removed from this browser too.`,
          });
        }
      } catch (e) {
        /* offline or DB unavailable — next round */
      }
    };
    void prune(true);
    const timer = window.setInterval(() => void prune(), SERVER_REFRESH_MS);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    navigator.storage?.estimate?.()
      .then((e) => setStorage({ usage: e.usage ?? 0, quota: e.quota ?? 0 }))
      .catch(() => undefined);
  }, [batches]);

  // Leaving while uploads are queued is safe (they resume next time), but the server lags until then.
  const activeOps = outbox.filter((o) => o.nextAttemptAt < Number.MAX_SAFE_INTEGER).length;
  useEffect(() => {
    if (!activeOps) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [activeOps]);

  const activeBatch = activeBatchId ? allLocalBatches.find((b) => b.id === activeBatchId) ?? null : null;

  useEffect(() => {
    if (activeBatch) void openBatch(activeBatch.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBatch?.id]);

  const unfinished = useMemo(
    () => batches.filter((b) => !b.completedAt && !b.serverDeletedAt).sort((a, b) => a.createdAt - b.createdAt),
    [batches]
  );
  const continueBatch = unfinished[0] ?? null;

  const nextBatch = useMemo(() => {
    if (!activeBatch || activeBatch.viewOnly) return null;
    const linked = activeBatch.nextBatchId
      ? batches.find((b) => b.id === activeBatch.nextBatchId && !b.completedAt && !b.serverDeletedAt)
      : null;
    return linked ?? unfinished.find((b) => b.id !== activeBatch.id) ?? null;
  }, [activeBatch, batches, unfinished]);

  const openModal = (batchId: string) => setSearchParams({ set: batchId });
  const closeModal = () => setSearchParams({});

  const progressOf = (b: ILocalBatch) => packDownloadProgress(b, filesByBatch[b.id] || []);

  // Bytes of files still in flight are not in the DB: repaint every second while something
  // downloads, so a slow download is visibly moving (and a stuck one visibly isn't).
  const [, setDownloadTick] = useState(0);
  const anyDownloading = allLocalBatches.some((b) => !b.blobsPurgedAt && !progressOf(b).complete);
  useEffect(() => {
    if (!anyDownloading) return;
    const timer = window.setInterval(() => setDownloadTick((n) => n + 1), 1000);
    return () => window.clearInterval(timer);
  }, [anyDownloading]);
  const downloadedText = (b: ILocalBatch) => {
    const pr = progressOf(b);
    return `${pr.done}/${pr.total} images · ${fmtBytes(pr.bytes + batchDownloadActivity(b.id).loaded)}`;
  };

  /** Decisions or edits that exist only in this browser — removing the set would lose them. */
  const hasUnsavedWork = (b: ILocalBatch) =>
    outbox.some((o) => o.batchId === b.id)
    || (filesByBatch[b.id] || []).some((f) => {
      const saved = !!f.decision && f.sync.state === 'synced';
      return (!!f.decision && !saved) || (!!f.edit && !saved);
    });

  const handleRemove = (batchId: string) => {
    if (confirmRemoveId !== batchId) {
      setConfirmRemoveId(batchId);
      window.setTimeout(() => setConfirmRemoveId((cur) => (cur === batchId ? null : cur)), 4000);
      return;
    }
    setConfirmRemoveId(null);
    if (activeBatchId === batchId) closeModal();
    void removeLocalPack(batchId);
  };

  /** A pack opens only once all of it is in this browser — switching files then never waits. */
  const openPack = (batchId: string) => {
    const b = allLocalBatches.find((x) => x.id === batchId);
    if (b && progressOf(b).complete) openModal(batchId);
  };

  /** `own` — the designer's own set taken in another browser: it is loaded to keep reviewing, not just to look at. */
  const handleLoadFromServer = async (batchId: string, own: boolean) => {
    setLoadingPackId(batchId);
    setMessage(null);
    try {
      if (own) await loadOwnPack(batchId);
      else await loadPackForViewing(batchId);
    } catch (e: any) {
      setMessage({ tone: 'error', text: `Could not load set #${batchId}: ${e?.message || e}` });
    } finally {
      setLoadingPackId(null);
    }
  };

  // The same designer may review a set in two browsers: when the list shows that an own set
  // changed on the server, pull those reviews in (once per server state; unsaved local decisions win).
  const syncedStatesRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    (server.data?.result ?? []).forEach((row) => {
      const local = allLocalBatches.find((x) => x.id === row.printfileBatchId);
      if (!local || local.viewOnly || local.serverDeletedAt) return;
      const saved = (filesByBatch[local.id] || []).filter((f) => f.decision && f.sync.state === 'synced').length;
      const differs = row.status !== local.serverStatus
        || row.reviewedFileCount !== saved
        || (!!local.serverUpdatedAt && row.updatedAt !== local.serverUpdatedAt);
      if (!differs || outbox.some((o) => o.batchId === local.id)) return;
      const state = `${row.printfileBatchId}|${row.status}|${row.updatedAt}|${row.reviewedFileCount}`;
      if (syncedStatesRef.current.has(state)) return;
      syncedStatesRef.current.add(state);
      void syncOwnPackFromServer(local.id).catch(() => undefined);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [server.data]);

  /** Action cell of "All packs": open / view / load, depending on role and what is in this browser. */
  const renderServerAction = (b: IPrintFileBatchSummary) => {
    const id = b.printfileBatchId;
    const local = allLocalBatches.find((x) => x.id === id);
    const mineByName = !!myName && (b.createdBy || '').trim().toLowerCase() === myName;
    const mine = (!!local && !local.viewOnly) || mineByName;
    // An own set that is not here as a reviewable copy yet (it was taken in another browser).
    const loadAsOwn = mineByName && (!local || !!local.viewOnly);
    const continueHere = loadAsOwn && b.status !== 'done';
    if (!canViewAny && !mine) {
      return (
        <span className="text-[11px] text-gray-500" title="Only the designer who took this set can open it">
          not your set
        </span>
      );
    }
    const busy = loadingPackId === id;
    if (local && !local.blobsPurgedAt && !continueHere) {
      const pr = progressOf(local);
      if (!pr.complete) {
        return (
          <div className="min-w-[150px] ml-auto">
            <ProgressBar done={pr.done} total={pr.total} />
            <div className="flex items-center justify-between gap-2 text-[11px] text-gray-400 mt-1">
              <span>{downloadedText(local)}</span>
              {local.viewOnly && (
                <button
                  type="button"
                  onClick={() => void removeLocalPack(id)}
                  title="Stop and remove this set from this browser"
                  className="bg-transparent border-0 p-0 text-gray-400 hover:text-white cursor-pointer"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        );
      }
      const decided = (filesByBatch[id] || []).filter((f) => f.decision).length;
      // Server timestamps are compared with server timestamps only (the server clock is not ours).
      const stale = !!local.viewOnly && (
        b.status !== local.serverStatus
        || b.reviewedFileCount !== decided
        || (!!local.serverUpdatedAt && b.updatedAt !== local.serverUpdatedAt)
      );
      return (
        <div className="flex gap-1.5 justify-end">
          {stale && (
            <button
              type="button"
              onClick={() => void handleLoadFromServer(id, false)}
              disabled={busy}
              title="The set changed on the server since it was loaded"
              className={`${smallBtn} bg-slate-700 hover:bg-slate-600 text-white`}
            >
              {busy ? 'Loading…' : 'Update'}
            </button>
          )}
          <button type="button" onClick={() => openPack(id)} className={`${smallBtn} bg-pink-600 hover:bg-pink-700 text-white`}>
            {local.viewOnly || b.status === 'done' || local.serverStatus === 'done' ? 'View' : 'Open'}
          </button>
        </div>
      );
    }
    if (local?.blobsPurgedAt && !continueHere) {
      return (
        <button type="button" onClick={() => void redownloadPack(id)} className={`${smallBtn} bg-slate-700 hover:bg-slate-600 text-white`}>
          Download again
        </button>
      );
    }
    return (
      <button
        type="button"
        onClick={() => void handleLoadFromServer(id, loadAsOwn)}
        disabled={busy}
        title={continueHere
          ? 'Your set from another browser: download it here with the decisions already saved and keep reviewing'
          : 'Download the set into this browser to view it'}
        className={`${smallBtn} ${continueHere ? 'bg-pink-600 hover:bg-pink-700' : 'bg-slate-700 hover:bg-slate-600'} text-white`}
      >
        {busy ? 'Loading…' : continueHere ? 'Load to continue' : 'Load to view'}
      </button>
    );
  };

  const handleCreate = async () => {
    setCreating(true);
    setMessage(null);
    try {
      const created = await createBatch();
      if (!created) setMessage({ tone: 'info', text: 'No eligible print files right now — everything pending is already in a set or reviewed.' });
      else setMessage({ tone: 'info', text: `Set #${created.id} is downloading to this browser — open it once it is fully downloaded.` });
      void loadServer();
    } catch (e: any) {
      setMessage({ tone: 'error', text: `Could not get a new set: ${e?.message || e}` });
    } finally {
      setCreating(false);
    }
  };

  const handleRequestNext = async () => {
    if (!activeBatch) return;
    setCreating(true);
    try {
      // The new pack downloads in the background; the review offers it once it is complete.
      await createNextBatch(activeBatch.id);
      void loadServer();
    } catch (e: any) {
      setMessage({ tone: 'error', text: `Could not get the next set: ${e?.message || e}` });
    } finally {
      setCreating(false);
    }
  };

  const syncErrors = outbox.filter((o) => o.lastError).length;
  const waitingManual = outbox.filter((o) => o.lastError && o.nextAttemptAt >= PARKED).length;
  const syncNote = retriesStopped ? 'auto-retry stopped' : waitingManual === syncErrors ? 'waiting for Retry now' : 'retrying';
  const counts = useMemo(() => {
    const map: Record<string, number> = {};
    server.data?.countsByStatus.forEach((c) => { map[c.status] = c.count; });
    return map;
  }, [server.data]);
  const totalAll = PRINT_FILE_BATCH_STATUSES.reduce((sum, s) => sum + (counts[s] || 0), 0);
  const serverPages = server.data ? Math.max(1, Math.ceil(server.data.totalCount / (server.data.perPage || SERVER_PAGE_SIZE))) : 1;

  return (
    <PageContainer contentStyle={{ padding: 0 }}>
      <div className="print-ready-page p-4 md:p-6 min-h-[calc(100vh-120px)]" data-theme={theme.palette.mode} style={cssVars}>
        <div className="max-w-6xl mx-auto flex flex-col gap-5">
          <header className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-violet-500 m-0">
                Print File Review
              </h1>
              <p className="text-gray-400 text-sm mt-1 mb-0">
                Take a set of ~10 print files, review or edit each one. Every decision is saved to the server in the background.
              </p>
            </div>
            <div className="flex items-center gap-2 text-xs">
              {outbox.length === 0 ? (
                <span className="px-3 py-1.5 rounded-lg bg-green-600/20 text-green-300">All changes saved</span>
              ) : (
                <span
                  title={`Failed saves are retried automatically up to ${MAX_AUTO_ATTEMPTS} times, then wait for Retry now`}
                  className={`px-3 py-1.5 rounded-lg ${syncErrors ? 'bg-red-600/20 text-red-300' : 'bg-blue-600/20 text-blue-200'}`}
                >
                  {syncErrors ? `${syncErrors} not saved yet (${syncNote})` : `Saving ${outbox.length}…`}
                </span>
              )}
              {syncErrors > 0 && (
                <button type="button" onClick={() => void retryAllNow()} className={`${smallBtn} bg-slate-700 hover:bg-slate-600 text-white`}>
                  Retry now
                </button>
              )}
              {retriesStopped ? (
                <button
                  type="button"
                  onClick={() => void setRetriesStopped(false)}
                  title="Failed saves are retried automatically again"
                  className={`${smallBtn} bg-amber-600/80 hover:bg-amber-600 text-white`}
                >
                  Resume retries
                </button>
              ) : syncErrors > 0 && (
                <button
                  type="button"
                  onClick={() => void setRetriesStopped(true)}
                  title="Stop retrying failed saves automatically — they wait for Retry now"
                  className={`${smallBtn} bg-slate-800 hover:bg-slate-700 text-gray-200`}
                >
                  Stop retries
                </button>
              )}
            </div>
          </header>

          {error && (
            <div className="rounded-xl px-4 py-3 text-sm bg-red-500/10 border border-red-500/30 text-red-200">
              Browser storage is unavailable ({error}). Review needs IndexedDB — a private window may block it.
            </div>
          )}
          {message && (
            <div className={`rounded-xl px-4 py-3 text-sm border flex items-start gap-3 ${message.tone === 'error' ? 'bg-red-500/10 border-red-500/30 text-red-200' : 'bg-blue-500/10 border-blue-500/30 text-blue-100'}`}>
              <span className="flex-1">{message.text}</span>
              <button type="button" onClick={() => setMessage(null)} className="bg-transparent border-0 text-gray-400 hover:text-white cursor-pointer">✕</button>
            </div>
          )}
          {activeBatchId && !loading && !activeBatch && (
            <div className="rounded-xl px-4 py-3 text-sm bg-amber-500/10 border border-amber-500/30 text-amber-100 flex items-center gap-3">
              <span className="flex-1">
                Set #{activeBatchId} is not in this browser yet. Find it in “All sets” and load it — your own sets can be continued here, others can be viewed by moderators.
              </span>
              <button type="button" onClick={closeModal} className={`${smallBtn} bg-slate-700 hover:bg-slate-600 text-white`}>OK</button>
            </div>
          )}

          {/* Actions */}
          <section className="glass-panel p-5 flex flex-wrap items-center gap-3">
            {continueBatch && (() => {
              const pr = progressOf(continueBatch);
              return pr.complete ? (
                <button
                  type="button"
                  onClick={() => openPack(continueBatch.id)}
                  className="btn-primary px-5 py-3 rounded-xl font-bold text-white shadow-lg border-0"
                >
                  Continue set #{continueBatch.id}
                  <span className="font-normal text-white/80 text-sm ml-2">
                    {batchStats(filesByBatch[continueBatch.id] || []).decided}/{continueBatch.fileIds.length} decided
                  </span>
                </button>
              ) : (
                <div className="px-5 py-3 rounded-xl bg-slate-800/70 min-w-[300px]" title="The set can be opened once all of it is in this browser">
                  <div className="flex items-baseline justify-between gap-3 mb-1.5">
                    <span className="text-sm font-semibold text-white">Set #{continueBatch.id} is downloading…</span>
                    <span className="text-[11px] text-gray-400 whitespace-nowrap">{downloadedText(continueBatch)}</span>
                  </div>
                  <ProgressBar done={pr.done} total={pr.total} />
                  {pr.reviewComplete && (
                    <button
                      type="button"
                      onClick={() => openModal(continueBatch.id)}
                      title="The review images are here; the files needed for editing are still downloading, so Edit may have to wait"
                      className={`${smallBtn} mt-2 bg-slate-700 hover:bg-slate-600 text-white`}
                    >
                      Open anyway
                    </button>
                  )}
                </div>
              );
            })()}
            <button
              type="button"
              onClick={() => void handleCreate()}
              disabled={creating || !!error}
              className={continueBatch
                ? 'px-5 py-3 rounded-xl font-semibold text-white bg-slate-700 hover:bg-slate-600 border-0 transition-colors disabled:opacity-50'
                : 'btn-primary px-5 py-3 rounded-xl font-bold text-white shadow-lg border-0 disabled:opacity-50'}
            >
              {creating ? 'Getting set…' : 'Get new set'}
            </button>
            <div className="ml-auto text-xs text-gray-400 text-right">
              <div>{batches.length} set{batches.length === 1 ? '' : 's'} in this browser</div>
              {storage && <div>Storage used: {fmtBytes(storage.usage)}{storage.quota ? ` of ${fmtBytes(storage.quota)}` : ''}</div>}
            </div>
          </section>

          {/* Local batches */}
          <section className="glass-panel p-5">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <h2 className="text-lg font-semibold text-white m-0 mr-2">In this browser</h2>
              {batches.length > 0 && (['all', ...PRINT_FILE_BATCH_STATUSES] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setLocalFilter(s)}
                  className={`${smallBtn} ${activeLocalFilter === s ? 'bg-pink-600 text-white' : 'bg-slate-800 hover:bg-slate-700 text-gray-300'}`}
                >
                  {s === 'all' ? 'All' : STATUS_STYLE[s].label}
                  <span className="ml-1 opacity-70">{s === 'all' ? batches.length : localCounts[s]}</span>
                </button>
              ))}
            </div>
            {loading ? (
              <div className="text-sm text-gray-400">Loading…</div>
            ) : batches.length === 0 ? (
              <div className="text-sm text-gray-400">No sets yet — get one to start reviewing.</div>
            ) : shownLocal.length === 0 ? (
              <div className="text-sm text-gray-400">No sets with this status in this browser.</div>
            ) : (
              <div className="overflow-auto max-h-[340px] rounded-lg">
                <table className="w-full text-sm text-left border-collapse">
                  <thead className="sticky top-0 z-10 bg-[#1b2233]">
                    <tr className="text-[11px] uppercase text-gray-400">
                      <th className="py-2 px-2 font-medium border-b border-white/10">Set</th>
                      <th className="py-2 px-2 font-medium border-b border-white/10">Received</th>
                      <th className="py-2 px-2 font-medium border-b border-white/10">Downloaded</th>
                      <th className="py-2 px-2 font-medium border-b border-white/10">Decisions</th>
                      <th className="py-2 px-2 font-medium border-b border-white/10">Saved on server</th>
                      <th className="py-2 px-2 font-medium border-b border-white/10">Status</th>
                      <th className="py-2 px-2 font-medium border-b border-white/10" />
                    </tr>
                  </thead>
                  <tbody>
                    {shownLocal.map((b: ILocalBatch) => {
                      const st = batchStats(filesByBatch[b.id] || []);
                      const pr = progressOf(b);
                      const canPurge = !!b.doneSyncedAt && !b.blobsPurgedAt;
                      const isDone = b.serverStatus === 'done';
                      return (
                        <tr key={b.id} className="text-gray-200 hover:bg-white/5">
                          <td className="py-2 px-2 border-b border-white/5">
                            <div className="font-semibold text-white">
                              #{b.id}
                              {b.serverDeletedAt && (
                                <span
                                  className="ml-2 px-1.5 py-0.5 rounded bg-red-600/30 text-red-200 text-[10px] font-semibold align-middle"
                                  title="The server deleted this set (not started within 30 minutes). Its local decisions were kept."
                                >
                                  Deleted on server
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-gray-500">
                              {st.total} files{b.prefetchedFor ? ` · prefetched during #${b.prefetchedFor}` : ''}
                            </div>
                          </td>
                          <td className="py-2 px-2 border-b border-white/5 whitespace-nowrap">{fmtDate(b.createdAt)}</td>
                          <td className="py-2 px-2 border-b border-white/5">
                            {b.blobsPurgedAt ? (
                              <>
                                <div className="text-gray-500">Images removed</div>
                                <button type="button" onClick={() => void redownloadPack(b.id)} className={`${smallBtn} mt-1 bg-slate-700 hover:bg-slate-600 text-white`}>
                                  Download again
                                </button>
                              </>
                            ) : pr.complete ? (
                              <>
                                <div className={pr.failed ? 'text-amber-200' : 'text-green-300'}>{pr.readyFiles}/{pr.files} files ready</div>
                                {pr.failed > 0 && (
                                  <button type="button" onClick={() => void downloadBatchAssets(b.id)} className={`${smallBtn} mt-1 bg-red-600/30 hover:bg-red-600/50 text-red-100`}>
                                    {pr.failed} image{pr.failed === 1 ? '' : 's'} failed — retry
                                  </button>
                                )}
                              </>
                            ) : (
                              <div className="min-w-[170px]">
                                <ProgressBar done={pr.done} total={pr.total} />
                                <div className="text-[11px] text-gray-400 mt-1">Downloading {downloadedText(b)}</div>
                                {pr.reviewComplete && (
                                  <button
                                    type="button"
                                    onClick={() => openModal(b.id)}
                                    title="The review images are here; the files needed for editing are still downloading, so Edit may have to wait"
                                    className={`${smallBtn} mt-1 bg-slate-700 hover:bg-slate-600 text-white`}
                                  >
                                    Open anyway
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="py-2 px-2 border-b border-white/5">
                            <div>{st.decided}/{st.total}</div>
                            {st.decided > 0 && (
                              <div className="text-[11px] text-gray-500">
                                <span className="text-green-400">{st.ok} ok</span> · <span className="text-red-400">{st.design} design</span> · <span className="text-amber-400">{st.cs} cs</span>
                                {st.edited ? ` · ${st.edited} edited` : ''}
                              </div>
                            )}
                          </td>
                          <td className="py-2 px-2 border-b border-white/5">
                            <span className={st.syncErrors ? 'text-red-300' : st.synced === st.decided ? 'text-green-300' : 'text-blue-200'}>
                              {st.synced}/{st.decided}
                            </span>
                            {st.syncErrors > 0 && <span className="text-[11px] text-red-300 ml-1">({st.syncErrors} failed)</span>}
                          </td>
                          <td className="py-2 px-2 border-b border-white/5"><StatusChip status={b.serverStatus} /></td>
                          <td className="py-2 px-2 border-b border-white/5">
                            <div className="flex gap-1.5 justify-end flex-wrap">
                              <button
                                type="button"
                                onClick={() => openPack(b.id)}
                                disabled={!pr.complete || !!b.blobsPurgedAt}
                                title={pr.complete ? undefined : 'Opens once the whole set is in this browser'}
                                className={`${smallBtn} bg-pink-600 hover:bg-pink-700 text-white`}
                              >
                                {isDone ? 'View' : b.completedAt ? 'Open' : st.decided ? 'Continue' : 'Review'}
                              </button>
                              <button type="button" onClick={() => void downloadBatchLogs(b.id)} className={`${smallBtn} bg-slate-700 hover:bg-slate-600 text-white`}>
                                Log
                              </button>
                              {(() => {
                                // A set the server deleted can always go; otherwise only when nothing would be lost.
                                const blocked = !b.serverDeletedAt && hasUnsavedWork(b);
                                const confirming = confirmRemoveId === b.id;
                                return (
                                  <button
                                    type="button"
                                    onClick={() => handleRemove(b.id)}
                                    disabled={blocked}
                                    title={blocked
                                      ? 'This set has decisions or edits that are not saved on the server yet'
                                      : 'Remove this set from this browser (it stays on the server and can be loaded again from “All sets”)'}
                                    className={`${smallBtn} ${confirming || b.serverDeletedAt ? 'bg-red-600/50 hover:bg-red-600/70 text-white' : 'bg-slate-800 hover:bg-slate-700 text-gray-300'}`}
                                  >
                                    {confirming ? 'Click again to remove' : 'Remove'}
                                  </button>
                                );
                              })()}
                              {canPurge && (
                                <button
                                  type="button"
                                  onClick={() => void purgeBatchImages(b.id)}
                                  title="Remove downloaded images of this finished set (decisions and log stay)"
                                  className={`${smallBtn} bg-slate-800 hover:bg-slate-700 text-gray-300`}
                                >
                                  Free space
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* All batches on the server */}
          <section className="glass-panel p-5">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <h2 className="text-lg font-semibold text-white m-0 mr-2">All sets</h2>
              {server.data?.pendingReviewCount != null && (
                <span
                  className="px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-200 text-xs font-semibold mr-1"
                  title="Print files waiting for design review"
                >
                  {server.data.pendingReviewCount.toLocaleString()} files waiting for review
                </span>
              )}
              {(['all', ...PRINT_FILE_BATCH_STATUSES] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => { setServerStatus(s); setServerPage(1); }}
                  className={`${smallBtn} ${serverStatus === s ? 'bg-pink-600 text-white' : 'bg-slate-800 hover:bg-slate-700 text-gray-300'}`}
                >
                  {s === 'all' ? 'All' : STATUS_STYLE[s].label}
                  <span className="ml-1 opacity-70">{s === 'all' ? totalAll : counts[s] || 0}</span>
                </button>
              ))}
              {serverUpdatedAt && (
                <span className="ml-auto text-[11px] text-gray-500" title="Refreshes automatically every minute while this page is open">
                  Updated {new Date(serverUpdatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </span>
              )}
              <button
                type="button"
                onClick={() => void loadServer()}
                disabled={server.loading}
                className={`${smallBtn} ${serverUpdatedAt ? '' : 'ml-auto '}bg-slate-700 hover:bg-slate-600 text-white`}
              >
                {server.loading ? 'Loading…' : 'Refresh'}
              </button>
            </div>
            {server.error ? (
              <div className="text-sm text-red-300">Failed to load: {server.error}</div>
            ) : !server.data?.result.length ? (
              <div className="text-sm text-gray-400">{server.loading ? 'Loading…' : 'No sets'}</div>
            ) : (
              <>
                <div className="overflow-auto max-h-[480px] rounded-lg">
                  <table className="w-full text-sm text-left border-collapse">
                    <thead className="sticky top-0 z-10 bg-[#1b2233]">
                      <tr className="text-[11px] uppercase text-gray-400">
                        <th className="py-2 px-2 font-medium border-b border-white/10">Set</th>
                        <th className="py-2 px-2 font-medium border-b border-white/10">Status</th>
                        <th className="py-2 px-2 font-medium border-b border-white/10">Created by</th>
                        <th className="py-2 px-2 font-medium border-b border-white/10">Created</th>
                        <th className="py-2 px-2 font-medium border-b border-white/10">Updated</th>
                        <th className="py-2 px-2 font-medium border-b border-white/10">Reviewed</th>
                        <th className="py-2 px-2 font-medium border-b border-white/10" />
                      </tr>
                    </thead>
                    <tbody>
                      {server.data.result.map((b) => {
                        const pct = b.fileCount ? Math.round((b.reviewedFileCount / b.fileCount) * 100) : 0;
                        const statsOpen = canViewAny && statsOpenId === b.printfileBatchId;
                        return (
                          <React.Fragment key={b.printfileBatchId}>
                          <tr className="text-gray-200 hover:bg-white/5">
                            <td className="py-2 px-2 border-b border-white/5 font-semibold text-white">#{b.printfileBatchId}</td>
                            <td className="py-2 px-2 border-b border-white/5"><StatusChip status={b.status} /></td>
                            <td className="py-2 px-2 border-b border-white/5">{b.createdBy || '—'}</td>
                            <td className="py-2 px-2 border-b border-white/5 whitespace-nowrap">{fmtDate(b.createdAt)}</td>
                            <td className="py-2 px-2 border-b border-white/5 whitespace-nowrap">{fmtDate(b.updatedAt)}</td>
                            <td className="py-2 px-2 border-b border-white/5 min-w-[140px]">
                              <div className="flex items-center gap-2">
                                <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
                                  <div className="h-full bg-green-500" style={{ width: `${pct}%` }} />
                                </div>
                                <span className="text-xs whitespace-nowrap">{b.reviewedFileCount}/{b.fileCount}</span>
                              </div>
                            </td>
                            <td className="py-2 px-2 border-b border-white/5 text-right">
                              <div className="flex gap-1.5 justify-end items-center">
                                {canViewAny && (
                                  <button
                                    type="button"
                                    onClick={() => setStatsOpenId(statsOpen ? null : b.printfileBatchId)}
                                    title="Review statistics of this set (moderators only)"
                                    className={`${smallBtn} ${statsOpen ? 'bg-violet-600 text-white' : 'bg-slate-800 hover:bg-slate-700 text-gray-200'}`}
                                  >
                                    Stats
                                  </button>
                                )}
                                {renderServerAction(b)}
                              </div>
                            </td>
                          </tr>
                          {statsOpen && (
                            <tr>
                              <td colSpan={7} className="px-2 pb-3 border-b border-white/5 bg-white/[0.03]">
                                <PackStatsPanel batchId={b.printfileBatchId} version={`${b.status}|${b.updatedAt}|${b.reviewedFileCount}`} />
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-end gap-2 mt-3 text-xs text-gray-400">
                  <button
                    type="button"
                    onClick={() => setServerPage((p) => Math.max(1, p - 1))}
                    disabled={serverPage <= 1 || server.loading}
                    className={`${smallBtn} bg-slate-800 hover:bg-slate-700 text-white`}
                  >
                    Prev
                  </button>
                  <span>Page {serverPage} of {serverPages}</span>
                  <button
                    type="button"
                    onClick={() => setServerPage((p) => Math.min(serverPages, p + 1))}
                    disabled={serverPage >= serverPages || server.loading}
                    className={`${smallBtn} bg-slate-800 hover:bg-slate-700 text-white`}
                  >
                    Next
                  </button>
                </div>
              </>
            )}
          </section>
        </div>

        {activeBatch && (
          <ReviewModal
            key={activeBatch.id}
            batch={activeBatch}
            files={filesByBatch[activeBatch.id] || []}
            nextBatch={nextBatch}
            nextBatchFiles={nextBatch ? filesByBatch[nextBatch.id] || [] : []}
            requestingNext={creating}
            retriesStopped={retriesStopped}
            showServerHistory={canViewAny}
            onClose={closeModal}
            onOpenBatch={openPack}
            onRequestNext={() => void handleRequestNext()}
          />
        )}
      </div>
    </PageContainer>
  );
};

export default PrintFileReviewPage;
